import { useEffect } from 'react';
import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes';
import { Capacitor } from '@capacitor/core';
import { isNative } from '@/lib/native/platform';

// App-wide theme provider (light / dark / system).
//
// Scope: the theme PREFERENCE is native-mobile only. The website (main site + desktop portal +
// web mobile-shell) must always render LIGHT, exactly as before the theme feature existed. We enforce
// that with next-themes' `forcedTheme`:
//   - web  → forcedTheme="light"  → next-themes will NOT add the `dark` class to <html>, and ignores
//            any OS colour-scheme or previously-stored "cbam-theme" value. The site is light, always.
//   - native → forcedTheme=undefined → the user's light / dark / system choice applies as normal.
//
// Wraps next-themes with the CBAM defaults (these still drive the native path):
//   - attribute="class"            → toggles the `dark` class on <html> (matches tailwind darkMode:["class"])
//   - defaultTheme="system"        → follows the phone/OS at night out of the box (Sam: don't burn his retinas in bed)
//   - enableSystem                 → tracks the OS colour-scheme when theme === "system"
//   - storageKey="cbam-theme"      → persists the explicit choice
//   - disableTransitionOnChange    → no colour-fade flash when switching
//
// sonner (src/components/ui/sonner.tsx) already reads next-themes' useTheme(); mounting this single
// provider above the app makes that hook resolve correctly (it previously always fell back to "system").
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const isNativePlatform = Capacitor.isNativePlatform();
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      storageKey="cbam-theme"
      disableTransitionOnChange
      forcedTheme={isNativePlatform ? undefined : 'light'}
    >
      <NativeStatusBarSync />
      {children}
    </NextThemesProvider>
  );
}

// Native-only (nice-to-have): keep the device status-bar legible against the resolved theme.
// On dark → light status-bar content (light text/icons) over the dark app chrome; on light → dark content.
// Dynamically imports @capacitor/status-bar so the web build never pulls it in, and no-ops on web.
function NativeStatusBarSync() {
  const { resolvedTheme } = useTheme();

  // Assert edge-to-edge overlay ONCE on mount (idempotent), separate from the theme-driven setStyle
  // effect below so it never re-fires on a light/dark switch. On Cap 8 / Android 15 (target-36) this
  // aligns the WebView with the forced SystemBars behaviour so env(safe-area-inset-*) resolve
  // non-zero. Guarded on a capability check + try/catch so a version without the method just no-ops.
  // We deliberately do NOT call setBackgroundColor / setOverlaysWebView(false) — those fight Cap 8
  // SystemBars / are no-ops under forced edge-to-edge.
  useEffect(() => {
    if (!isNative()) return;
    void (async () => {
      try {
        const { StatusBar } = await import('@capacitor/status-bar');
        if (typeof StatusBar.setOverlaysWebView === 'function') {
          await StatusBar.setOverlaysWebView({ overlay: true });
        }
      } catch {
        // Best-effort: never block render on a platform without the status-bar plugin.
      }
    })();
  }, []);

  useEffect(() => {
    if (!isNative() || !resolvedTheme) return;
    let cancelled = false;
    void (async () => {
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        if (cancelled) return;
        // Style.Dark = light content (for a dark bg); Style.Light = dark content (for a light bg).
        await StatusBar.setStyle({ style: resolvedTheme === 'dark' ? Style.Dark : Style.Light });
      } catch {
        // Status-bar styling is best-effort; never block render or throw on a platform without it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedTheme]);

  return null;
}
